import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  BEGINNER_CATEGORIES,
  BEGINNER_PLUGINS,
  EASY_START_SLUG,
  getBeginnerPlugin,
  getPluginsByCategory,
  type BeginnerCategory,
} from "../lib/beginner-plugins";
import { marketplaceApi, type MarketplaceWithPlugins } from "../lib/api";

// ─── Icons (inline SVGs for the light theme) ────────────────────────────────

function ImageIcon({ size = 24 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="m21 15-5-5L5 21" />
    </svg>
  );
}

function SearchIcon({ size = 24 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  );
}

function ShareIcon({ size = 24 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
    </svg>
  );
}

function MailIcon({ size = 24 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  );
}

function PencilIcon({ size = 24 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
    </svg>
  );
}

function VideoIcon({ size = 24 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="23 7 16 12 23 17 23 7" />
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
    </svg>
  );
}

function CheckCircleIcon({ size = 24 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}

function CopyIcon({ size = 24 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function ChevronDownIcon({ size = 24 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function ArrowRightIcon({ size = 24 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

const CATEGORY_ICONS: Record<string, React.FC<{ size?: number }>> = {
  image: ImageIcon,
  search: SearchIcon,
  share: ShareIcon,
  mail: MailIcon,
  pencil: PencilIcon,
  video: VideoIcon,
};

// ─── Copy Button ─────────────────────────────────────────────────────────────

function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition-all hover:bg-slate-800 active:scale-95"
    >
      {copied ? <CheckCircleIcon size={16} /> : <CopyIcon size={16} />}
      {copied ? "Copied!" : label}
    </button>
  );
}

// ─── Category Card ───────────────────────────────────────────────────────────

function CategoryCard({
  category,
  pluginCount,
  isActive,
  onClick,
}: {
  category: BeginnerCategory;
  pluginCount: number;
  isActive: boolean;
  onClick: () => void;
}) {
  const Icon = CATEGORY_ICONS[category.icon] || ImageIcon;

  return (
    <button
      onClick={onClick}
      className={`group relative rounded-2xl border-2 p-6 text-left transition-all duration-200 hover:shadow-lg ${
        isActive
          ? `${category.borderColor} ${category.bgColor} shadow-md`
          : "border-slate-200 bg-white hover:border-slate-300"
      }`}
    >
      <div
        className={`mb-4 inline-flex rounded-xl p-3 ${
          isActive ? category.bgColor : "bg-slate-100"
        }`}
      >
        <span className={isActive ? category.color : "text-slate-500"}>
          <Icon size={28} />
        </span>
      </div>
      <h3 className="mb-1 text-lg font-bold text-slate-900">{category.name}</h3>
      <p className="mb-3 text-sm text-slate-500">{category.description}</p>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-slate-400">
          {pluginCount} {pluginCount === 1 ? "tool" : "tools"}
        </span>
        <span
          className={`inline-flex items-center gap-1 text-xs font-medium transition-colors ${
            isActive ? category.color : "text-slate-400 group-hover:text-slate-600"
          }`}
        >
          {isActive ? "Selected" : "View tools"}
          <ChevronDownIcon size={14} />
        </span>
      </div>
    </button>
  );
}

// ─── Plugin Card (light theme) ───────────────────────────────────────────────

function BeginnerPluginCard({
  friendlyName,
  friendlyDescription,
  slug,
  category,
}: {
  friendlyName: string;
  friendlyDescription: string;
  slug: string;
  category: BeginnerCategory;
}) {
  const installCommand = `claude plugin install ${slug}@superchargeclaudecode`;
  const Icon = CATEGORY_ICONS[category.icon] || ImageIcon;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-200 hover:border-slate-300 hover:shadow-md">
      {/* Header */}
      <div className="mb-3 flex items-start gap-3">
        <div className={`rounded-xl p-2.5 ${category.bgColor}`}>
          <span className={category.color}>
            <Icon size={20} />
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="font-bold text-slate-900">{friendlyName}</h4>
          <Link
            to={`/plugin/${slug}`}
            className="text-xs text-slate-400 transition-colors hover:text-blue-500"
          >
            View details
          </Link>
        </div>
      </div>

      {/* Description */}
      <p className="mb-4 text-sm leading-relaxed text-slate-600">{friendlyDescription}</p>

      {/* Install */}
      <div className="rounded-xl bg-slate-50 p-3">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs font-medium text-slate-500">Install command:</span>
          <CopyButton text={installCommand} label="Copy" />
        </div>
        <code className="block break-all rounded-lg bg-slate-100 px-3 py-2 text-xs text-slate-700">
          {installCommand}
        </code>
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export function BeginnerMarketplacePage() {
  const [marketplace, setMarketplace] = useState<MarketplaceWithPlugins | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await marketplaceApi.getPublicMarketplace(EASY_START_SLUG);
        setMarketplace(data);
      } catch (err) {
        console.error("Failed to load easy-start marketplace:", err);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  // Count plugins per category — use API data if available, fallback to static
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    if (marketplace) {
      for (const plugin of marketplace.plugins) {
        const cat = plugin.category || "uncategorized";
        counts[cat] = (counts[cat] || 0) + 1;
      }
    } else {
      // Fallback to static data
      for (const plugin of BEGINNER_PLUGINS) {
        counts[plugin.category] = (counts[plugin.category] || 0) + 1;
      }
    }
    return counts;
  }, [marketplace]);

  // Get plugins for active category, merged with beginner-friendly names
  const activePlugins = useMemo(() => {
    if (!activeCategory) return [];

    // If API data loaded, merge with friendly names
    if (marketplace) {
      return marketplace.plugins
        .filter((p) => p.category === activeCategory)
        .map((p) => {
          const info = getBeginnerPlugin(p.slug);
          return {
            slug: p.slug,
            friendlyName: info?.friendlyName || p.name,
            friendlyDescription: info?.friendlyDescription || p.description,
          };
        });
    }

    // Fallback to static beginner plugin data
    return getPluginsByCategory(activeCategory).map((p) => ({
      slug: p.slug,
      friendlyName: p.friendlyName,
      friendlyDescription: p.friendlyDescription,
    }));
  }, [activeCategory, marketplace]);

  const activeCategoryInfo = activeCategory
    ? BEGINNER_CATEGORIES.find((c) => c.id === activeCategory)
    : null;

  const apiUrl =
    import.meta.env.VITE_API_URL ||
    (import.meta.env.PROD ? window.location.origin : "http://localhost:3001");

  const marketplaceCommand = `claude plugin marketplace add ${apiUrl}/api/marketplaces/${EASY_START_SLUG}/marketplace.json`;

  const handleCategoryClick = (categoryId: string) => {
    setActiveCategory(activeCategory === categoryId ? null : categoryId);
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-slate-50 to-white"
      style={{ color: "#1e293b" }}
    >
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-purple-600">
              <span className="text-sm font-bold text-white">S</span>
            </div>
            <span className="text-lg font-bold text-slate-900">Easy Start</span>
          </Link>
          <nav className="flex items-center gap-4">
            <a
              href="#tools"
              className="text-sm text-slate-500 transition-colors hover:text-slate-900"
            >
              Tools
            </a>
            <a
              href="#setup"
              className="text-sm text-slate-500 transition-colors hover:text-slate-900"
            >
              Get Started
            </a>
            <Link
              to="/"
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
            >
              Advanced View
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden pb-16 pt-20">
        {/* Background decoration */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -right-32 -top-32 h-96 w-96 rounded-full bg-blue-100/50 blur-3xl" />
          <div className="absolute -left-32 bottom-0 h-96 w-96 rounded-full bg-purple-100/50 blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-6xl px-4 text-center sm:px-6">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-blue-50 px-4 py-1.5 text-sm font-medium text-blue-700">
            <CheckCircleIcon size={16} />
            No coding required
          </div>
          <h1 className="mb-4 text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl md:text-6xl">
            AI tools made{" "}
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              simple
            </span>
          </h1>
          <p className="mx-auto mb-8 max-w-2xl text-lg text-slate-500 sm:text-xl">
            Create images, write content, manage social media, and more. Just install once and start
            using powerful AI tools with plain-English commands.
          </p>
          <div className="mx-auto max-w-xl">
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-lg">
              <p className="mb-2 text-xs font-medium text-slate-400">
                One command to install everything:
              </p>
              <div className="flex items-center gap-2">
                <code className="min-w-0 flex-1 truncate rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
                  {marketplaceCommand}
                </code>
                <CopyButton text={marketplaceCommand} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-slate-200 bg-white py-8">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-8 px-4 sm:gap-16 sm:px-6">
          {[
            { value: "18", label: "AI Tools" },
            { value: "6", label: "Categories" },
            { value: "Free", label: "To Use" },
            { value: "0", label: "Coding Needed" },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="text-2xl font-extrabold text-slate-900">{stat.value}</p>
              <p className="text-sm text-slate-500">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section id="tools" className="py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="mb-10 text-center">
            <h2 className="mb-3 text-3xl font-extrabold text-slate-900">What do you want to do?</h2>
            <p className="text-lg text-slate-500">Pick a category to see the AI tools available</p>
          </div>

          {isLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div
                  key={i}
                  className="animate-pulse rounded-2xl border border-slate-200 bg-white p-6"
                >
                  <div className="mb-4 h-14 w-14 rounded-xl bg-slate-100" />
                  <div className="mb-2 h-5 w-32 rounded bg-slate-100" />
                  <div className="mb-3 h-4 w-full rounded bg-slate-100" />
                  <div className="h-3 w-16 rounded bg-slate-100" />
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {BEGINNER_CATEGORIES.map((cat) => (
                  <CategoryCard
                    key={cat.id}
                    category={cat}
                    pluginCount={
                      categoryCounts[cat.id] ||
                      BEGINNER_PLUGINS.filter((p) => p.category === cat.id).length
                    }
                    isActive={activeCategory === cat.id}
                    onClick={() => handleCategoryClick(cat.id)}
                  />
                ))}
              </div>

              {/* Expanded category plugins */}
              {activeCategory && activeCategoryInfo && (
                <div className="mt-8 animate-fade-in">
                  <div className="mb-6 flex items-center gap-3">
                    <div className={`rounded-xl p-2.5 ${activeCategoryInfo.bgColor}`}>
                      <span className={activeCategoryInfo.color}>
                        {(() => {
                          const Icon = CATEGORY_ICONS[activeCategoryInfo.icon] || ImageIcon;
                          return <Icon size={24} />;
                        })()}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900">
                        {activeCategoryInfo.name} Tools
                      </h3>
                      <p className="text-sm text-slate-500">
                        {activePlugins.length} {activePlugins.length === 1 ? "tool" : "tools"}{" "}
                        available
                      </p>
                    </div>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {activePlugins.map((plugin) => (
                      <BeginnerPluginCard
                        key={plugin.slug}
                        friendlyName={plugin.friendlyName}
                        friendlyDescription={plugin.friendlyDescription}
                        slug={plugin.slug}
                        category={activeCategoryInfo}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Getting Started */}
      <section id="setup" className="border-t border-slate-200 bg-slate-50 py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="mb-10 text-center">
            <h2 className="mb-3 text-3xl font-extrabold text-slate-900">Get started in 3 steps</h2>
            <p className="text-lg text-slate-500">You'll be up and running in under 5 minutes</p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                step: "1",
                title: "Install Claude Code",
                description:
                  "Claude Code is a free AI tool from Anthropic. Install it with this command in your terminal.",
                command: "npm install -g @anthropic-ai/claude-code",
                note: "You'll need Node.js installed first. Download it from nodejs.org",
                color: "bg-blue-500",
              },
              {
                step: "2",
                title: "Add the Easy Start tools",
                description:
                  "This adds all the beginner-friendly tools to your Claude Code. Just copy, paste, and press Enter.",
                command: marketplaceCommand,
                note: null,
                color: "bg-purple-500",
              },
              {
                step: "3",
                title: "Start using tools",
                description:
                  'Open Claude Code and ask it to do anything! For example: "Create an image of a sunset over mountains".',
                command: "claude",
                note: "Just type 'claude' in your terminal and start chatting",
                color: "bg-green-500",
              },
            ].map((item) => (
              <div key={item.step} className="rounded-2xl border border-slate-200 bg-white p-6">
                <div
                  className={`mb-4 inline-flex h-10 w-10 items-center justify-center rounded-xl text-lg font-bold text-white ${item.color}`}
                >
                  {item.step}
                </div>
                <h3 className="mb-2 text-lg font-bold text-slate-900">{item.title}</h3>
                <p className="mb-4 text-sm text-slate-500">{item.description}</p>
                <div className="rounded-xl bg-slate-50 p-3">
                  <div className="mb-2 flex justify-end">
                    <CopyButton text={item.command} />
                  </div>
                  <code className="block break-all rounded-lg bg-slate-100 px-3 py-2 text-xs text-slate-700">
                    {item.command}
                  </code>
                </div>
                {item.note && <p className="mt-3 text-xs text-slate-400">{item.note}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ / Common Questions */}
      <section className="py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <h2 className="mb-8 text-center text-3xl font-extrabold text-slate-900">
            Common questions
          </h2>
          <div className="space-y-4">
            {[
              {
                q: "Do I need to know how to code?",
                a: 'No! Once Claude Code is installed, you just type what you want in plain English. For example: "Create an image of a cat wearing sunglasses" or "Audit my website\'s SEO".',
              },
              {
                q: "Is this free?",
                a: "The tools and marketplace are free. Claude Code itself requires an Anthropic API subscription (claude.ai/code). Most of these AI tools also have their own usage costs for generating images, videos, etc.",
              },
              {
                q: "What is Claude Code?",
                a: "Claude Code is an AI assistant from Anthropic that runs in your terminal (command line). It can read your files, run commands, and use plugins/tools to do creative and technical work for you.",
              },
              {
                q: "How do I install a single tool?",
                a: "After adding the marketplace (step 2 above), run: claude plugin install <tool-name>@superchargeclaudecode — replacing <tool-name> with the tool you want. Or just add the whole marketplace to get everything.",
              },
            ].map((item) => (
              <details key={item.q} className="group rounded-2xl border border-slate-200 bg-white">
                <summary className="flex cursor-pointer items-center justify-between p-5 text-left font-bold text-slate-900">
                  {item.q}
                  <ChevronDownIcon size={20} />
                </summary>
                <div className="px-5 pb-5 text-sm leading-relaxed text-slate-600">{item.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-slate-200 bg-gradient-to-br from-blue-50 to-purple-50 py-16">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6">
          <h2 className="mb-4 text-3xl font-extrabold text-slate-900">Ready to get started?</h2>
          <p className="mb-8 text-lg text-slate-500">
            Install the marketplace and start creating with AI today.
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <a
              href="#setup"
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-6 py-3 text-base font-bold text-white transition-colors hover:bg-slate-800"
            >
              Get Started
              <ArrowRightIcon size={18} />
            </a>
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-6 py-3 text-base font-medium text-slate-700 transition-colors hover:bg-slate-50"
            >
              View Advanced Marketplace
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white py-8">
        <div className="mx-auto max-w-6xl px-4 text-center sm:px-6">
          <p className="text-sm text-slate-400">
            Part of{" "}
            <Link to="/" className="text-blue-500 hover:text-blue-600">
              superchargeclaudecode.com
            </Link>{" "}
            &mdash; the Claude Code plugin marketplace
          </p>
        </div>
      </footer>
    </div>
  );
}
